/* eslint-disable restrict-imports/restrict-imports */
import React, { useState, useEffect, useCallback } from 'react';
import { TextBasedSpeedAuditEditModes } from 'react-scale/components/audits/SpeedAudit/TextBasedSpeedAuditDialog';
// eslint-disable-next-line restrict-imports/restrict-imports
import { Button, CircularProgress, Tooltip, IconButton } from 'design-system';
import { makeStyles } from 'design-system/product/theme';
import { H2 } from 'design-system/Typography';
import SubmitAuditModal from './SubmitAuditModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QAOperationHeader } from 'react-scale/components/remoAdmin/qaOperation/QAOperationSubmitModal';
import { useQAOperationContext } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import TextBasedSpeedAuditSettingsModal from './TextBasedSpeedAuditSettingsModal';
import { GridColumns } from '@mui/x-data-grid-pro';
import { IMandatoryColumn } from '@scale/shared/types/corp/speedAuditColumnConfig';
const AUTOSAVE_FREQUENCY_SECONDS = 60;
const TEXT_BASED_SPEED_AUDIT_DOCUMENTATION =
  'https://scale.atlassian.net/wiki/spaces/OP/pages/437846019/Text+Based+Speed+Audit';

// eslint-disable-next-line complexity
function TextBasedSpeedAuditHeader({
  name,
  id,
  completed,
  projectId,
  mode,
  setMode,
  loading,
  saveAudit,
  completeAudit,
  cellFontSize,
  setCellFontSize,
  saveColumnConfig,
  collapseRows,
  setCollapseRows,
  canEditResponses,
  isPodHealth = false,
  setIsAuditing = (_isAuditing: boolean) => {},
  setShowManageColumns,
  exportDataAsCsv,
  setIsFindAndReplaceMode,
  updateCompletionCounts,
  mandatoryColumns,
  apiRef,
  columns,
  usesPipelineV3,
}: {
  name: string;
  id: string;
  numObjects: number;
  completed: boolean;
  projectId: string;
  mode: string | undefined;
  setMode: (s: any) => void;
  loading: boolean;
  saveAudit: () => void;
  completeAudit: (actions: Record<string, any>, sendFeedback: boolean) => void;
  cellFontSize: number;
  setCellFontSize: (val: number) => void;
  saveColumnConfig: () => void;
  collapseRows: boolean;
  setCollapseRows: React.Dispatch<React.SetStateAction<boolean>>;
  canEditResponses: boolean;
  isPodHealth?: boolean;
  setIsAuditing?: (isAuditing: boolean) => void;
  setShowManageColumns: React.Dispatch<React.SetStateAction<boolean>>;
  exportDataAsCsv: () => void;
  setIsFindAndReplaceMode: (val: boolean) => void;
  updateCompletionCounts: () => Record<string, number>;
  mandatoryColumns: IMandatoryColumn;
  apiRef: $FIXME;
  columns: GridColumns;
  usesPipelineV3: boolean;
}) {
  const classes = useStyles();
  const [submitModalOpen, setSubmitModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<Date>();
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [completionCounts, setCompletionCounts] = useState<Record<string, number>>({});

  const [isOperationModalOpen, setIsOperationModalOpen] = useState(false);
  const closeOperationModal = useCallback(() => setIsOperationModalOpen(false), []);
  const { qaOperation, isActive, isInReview } = useQAOperationContext();

  const saveHelper = useCallback(async () => {
    setIsSaving(true);
    await saveAudit();
    setIsSaving(false);
    setLastSaved(new Date());
  }, [saveAudit]);

  useEffect(() => {
    if (!completed) {
      const interval = setInterval(async () => {
        await saveHelper();
      }, AUTOSAVE_FREQUENCY_SECONDS * 1000);
      return () => clearInterval(interval);
    }
  }, [saveHelper, completed]);

  const regularSubmit: boolean = !qaOperation || (!isInReview() && isActive());

  const onOpenCompletionModal = () => {
    const updatedCompletionCounts = updateCompletionCounts();
    setCompletionCounts(updatedCompletionCounts);
    setSubmitModalOpen(true);
  };

  return (
    <div className={classes.header}>
      {qaOperation && (
        <QAOperationHeader
          isOpen={isOperationModalOpen}
          overrideSubmitButton={!isInReview()}
          onClose={closeOperationModal}
        />
      )}
      {regularSubmit && submitModalOpen && (
        <SubmitAuditModal
          isOpen={submitModalOpen}
          setIsOpen={setSubmitModalOpen}
          setIsSubmitting={setIsSubmitting}
          completeAudit={completeAudit}
          canEditResponses={canEditResponses}
          completionCounts={completionCounts}
          mandatoryColumns={mandatoryColumns}
          apiRef={apiRef}
          columns={columns}
          usesPipelineV3={usesPipelineV3}
        />
      )}
      <div className={classes.headerRow}>
        <div className={classes.titleGroup}>
          <H2>Text Based Speed Audit</H2>
          <Tooltip style={{ cursor: 'pointer' }} title={'Click to view documentation'} arrow>
            <IconButton
              size="small"
              onClick={() => {
                window.open(TEXT_BASED_SPEED_AUDIT_DOCUMENTATION, '_blank');
              }}
            >
              <FontAwesomeIcon icon={'info-circle'} />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.buttonGroup}>
          <Button
            className={classes.button}
            onClick={() => setShowSettingsModal(true)}
            variant="outlined"
          >
            <div className={classes.buttonWithIcon}>
              <FontAwesomeIcon size="sm" icon="cog" />
              Settings
            </div>
          </Button>
          <Tooltip title="Save the current changes">
            <Button
              className={classes.button}
              onClick={saveHelper}
              disabled={completed}
              variant="outlined"
            >
              Save Audit
              {isSaving && (
                <CircularProgress
                  size={12}
                  color="secondary"
                  thickness={6}
                  className={classes.circularProgress}
                />
              )}
            </Button>
          </Tooltip>
          {(!qaOperation || !isInReview()) && (
            <Button
              className={classes.completeButton}
              onClick={onOpenCompletionModal}
              disabled={completed}
            >
              Complete Audit
              {isSubmitting && (
                <CircularProgress
                  size={12}
                  color="secondary"
                  thickness={6}
                  className={classes.circularProgress}
                />
              )}
            </Button>
          )}
          {isPodHealth && (
            <Button
              className={classes.button}
              onClick={() => setIsAuditing(false)}
              disabled={completed}
              variant="outlined"
            >
              Close Speed Audit
            </Button>
          )}
        </div>
      </div>
      <br />
      <div className={classes.headerRow}>
        <div className={classes.textGroup}>
          <div>
            Audit Batch:{' '}
            <b>
              {name} ({id})
            </b>
          </div>
          <div>
            Project ID: <b>{projectId}</b>
          </div>
        </div>
        <div className={classes.buttonGroup}>
          <Button onClick={() => setIsFindAndReplaceMode(true)}>
            <div className={classes.buttonWithIcon}>
              <FontAwesomeIcon size="sm" icon="search" />
              Find and Replace
            </div>
          </Button>
          <Button onClick={() => setShowManageColumns(true)}>
            <div className={classes.buttonWithIcon}>
              <FontAwesomeIcon size="sm" icon="pause" />
              Manage Columns
            </div>
          </Button>
          <Button onClick={exportDataAsCsv}>
            <div className={classes.buttonWithIcon}>
              <FontAwesomeIcon size="sm" icon="download" />
              Export Table
            </div>
          </Button>
        </div>
      </div>
      <br />
      <div className={classes.headerRow}>
        Last Saved: {lastSaved ? lastSaved.toLocaleString() : null}
        <div className={classes.buttonGroup}>
          <Button onClick={() => setCollapseRows(b => !b)}>
            <div className={classes.buttonWithIcon}>
              <FontAwesomeIcon size="sm" icon={collapseRows ? 'expand-alt' : 'compress-alt'} />
              {collapseRows ? 'Expand ' : 'Collapse '}rows
            </div>
          </Button>
          {!mode && (
            <Button onClick={() => setMode(TextBasedSpeedAuditEditModes.Edit)}>
              <div className={classes.buttonWithIcon}>
                <FontAwesomeIcon size="sm" icon="sparkles" />
                LLM Editor
              </div>
            </Button>
          )}
          {!mode && (
            <Button onClick={() => setMode(TextBasedSpeedAuditEditModes.Flag)}>
              <div className={classes.buttonWithIcon}>
                <FontAwesomeIcon size="sm" icon="pennant" />
                LLM Flag
              </div>
            </Button>
          )}
          {mode && (
            <Button
              variant="outlined"
              className={classes.button}
              disabled={loading}
              onClick={() => setMode(undefined)}
            >
              Finish {mode === 'edit' ? 'Editing' : 'Flagging'}
            </Button>
          )}
        </div>
      </div>
      <TextBasedSpeedAuditSettingsModal
        isOpen={showSettingsModal}
        setIsOpen={setShowSettingsModal}
        cellFontSize={cellFontSize}
        setCellFontSize={setCellFontSize}
        projectId={projectId}
        saveColumnConfig={saveColumnConfig}
        columns={columns}
      />
    </div>
  );
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  titleGroup: {
    display: 'flex',
    gap: '4px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '16px',
  },
  completeButton: {
    padding: '6px 16px !important',
    color: 'white',
    background: 'rgb(25, 118, 210, 1)',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.75)',
    },
  },
  button: {
    color: 'rgb(25, 118, 210, 1)',
    background: 'white',
    borderColor: 'rgb(25, 118, 210, 1)',
    padding: '6px 16px !important',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.25)',
    },
  },
  textButton: {
    color: 'rgb(25, 118, 210, 1)',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  buttonWithIcon: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  textGroup: {
    display: 'flex',
    gap: '24px',
    maxWidth: '70%',
  },
  settingsTextGroup: {
    display: 'flex',
    width: '75%',
    gap: '24px',
  },
  settingsText: {
    alignSelf: 'center',
    whiteSpace: 'nowrap',
  },
  settingsAction: {
    alignSelf: 'center',
    flexGrow: 1,
    textAlign: 'center',
  },
  slider: {
    minWidth: 400,
    color: 'rgb(25, 118, 210, 1)',
  },
  circularProgress: {
    marginLeft: '8px',
  },
  shortcut: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  shortcutText: {
    marginLeft: '12px',
    fontSize: '14px',
    lineHeight: '24px',
  },
  settingsDialog: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});

export default TextBasedSpeedAuditHeader;
